import { useEffect, useState, useCallback } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RefreshCw, X } from 'lucide-react';

// Check for updates every 60 seconds to catch new deployments quickly
const SW_UPDATE_INTERVAL_MS = 60 * 1000;

export function PWAUpdatePrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const isDev = import.meta.env.DEV;

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, registration) {
      if (isDev || !registration) return;
      console.log('[PWA] SW registered:', swUrl);

      // Periodic update check - catches new deployments even if app stays open
      const intervalId = setInterval(async () => {
        if (registration.installing || !navigator) return;
        
        // For iOS standalone mode, be more aggressive
        if ('connection' in navigator && !(navigator as any).onLine) return;

        try {
          const resp = await fetch(swUrl, {
            cache: 'no-store',
            headers: { 'cache-control': 'no-cache' },
          });

          if (resp?.status === 200) {
            await registration.update();
          }
        } catch (e) {
          console.log('[PWA] Update check failed:', e);
        }
      }, SW_UPDATE_INTERVAL_MS);

      return () => clearInterval(intervalId);
    },
    onRegisterError(error) {
      if (!isDev) console.error('[PWA] SW registration error:', error);
    },
  });

  // When a new SW is ready, show update prompt
  // For non-interactive updates (background), auto-apply after a short delay
  useEffect(() => {
    if (!needRefresh) return;

    // Check if user is actively using the app
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as any).standalone === true;

    if (isStandalone) {
      // In standalone PWA mode, show the prompt so user knows about the update
      setShowPrompt(true);

      // Auto-apply after 10 seconds if user doesn't interact
      const autoApplyTimer = setTimeout(() => {
        handleUpdate();
      }, 10000);

      return () => clearTimeout(autoApplyTimer);
    } else {
      // In browser mode, apply immediately
      updateServiceWorker(true);
    }
  }, [needRefresh]);

  const handleUpdate = useCallback(() => {
    // Clear all caches before reloading to ensure fresh assets
    if ('caches' in window) {
      caches.keys().then(names => {
        Promise.all(names.map(name => caches.delete(name))).then(() => {
          updateServiceWorker(true);
        });
      });
    } else {
      updateServiceWorker(true);
    }
    setShowPrompt(false);
  }, [updateServiceWorker]);

  const handleDismiss = () => {
    setShowPrompt(false);
    setNeedRefresh(false);
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96">
      <Card className="p-4 shadow-lg border-primary">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <h3 className="font-semibold mb-1">업데이트 사용 가능</h3>
            <p className="text-sm text-muted-foreground mb-3">
              새로운 버전의 Gemurhood를 사용할 수 있습니다. 곧 자동으로 업데이트됩니다.
            </p>
            <div className="flex gap-2">
              <Button onClick={handleUpdate} size="sm" className="flex-1">
                <RefreshCw size={16} className="mr-2" />
                지금 업데이트
              </Button>
              <Button onClick={handleDismiss} variant="outline" size="sm">
                <X size={16} />
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
